import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import '@/App.css';

// Auth
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SessionWarning from './components/auth/SessionWarning';
// Note: AuthCallback removed - backend handles Google OAuth callback directly

// Components
import { ToastProvider } from './contexts/ToastContext';
import { Toaster } from './components/ui/sonner';

// Login Page
import LoginPage from './pages/Login';

// Pages
import Dashboard from './pages/Dashboard';
import AllContacts from './pages/Contacts/AllContacts';
import Organizations from './pages/Contacts/Organizations';
import SentimentScore from './pages/Contacts/SentimentScore';
import SegmentsTags from './pages/Contacts/SegmentsTags';
import ReferralMap from './pages/Contacts/ReferralMap';
import { SoftCircle, HardCircle } from './pages/Pipeline';
import { Tasks, Calendar as CalendarPage, InteractionLogging } from './pages/Activities';
import { Templates, Inbox } from './pages/Comms';
import Analytics from './pages/Analytics';
import Network from './pages/Network';

// Icons
import {
  Users,
  Settings,
  Menu,
  X,
  User,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

// Custom Icons
import { 
  DashboardIcon, 
  AnalyticsIcon, 
  SavedViewsIcon, 
  LogoutIcon,
  InvestorsIcon,
  OrganizationIcon,
  SentimentIcon,
  TagsIcon,
  ReferralMapIcon,
  PipelineIcon,
  SoftCircleIcon,
  HardCircleIcon,
  ActivitiesIcon,
  TasksIcon,
  InteractionIcon,
  CalendarIconCustom,
  CommsHubIcon,
  InboxIconCustom,
  TemplateIcon,
  AutomationsIcon,
  NetworkIcon,
  OpsComplianceIcon
} from './components/CustomIcons';

// Utility function for classnames
const cn = (...classes) => classes.filter(Boolean).join(' ');

// Width constants
const COLLAPSED_WIDTH = 'w-16';  // 64px - icons only
const EXPANDED_WIDTH = 'w-60';   // 240px - icons + labels

// Navigation items with nested structure
const navItems = [
  { 
    path: '/', 
    label: 'Dashboard', 
    icon: DashboardIcon 
  },
  { 
    path: '/saved-views', 
    label: 'Saved Views', 
    icon: SavedViewsIcon 
  },
  { 
    label: 'Contacts', 
    icon: Users,
    children: [
      { path: '/contacts/all', label: 'Investors', icon: InvestorsIcon },
      { path: '/contacts/organizations', label: 'Organizations', icon: OrganizationIcon },
      { path: '/contacts/sentiment-score', label: 'Sentiment Score', icon: SentimentIcon },
      { path: '/contacts/segments', label: 'Segments & Tags', icon: TagsIcon },
      { path: '/contacts/referral-map', label: 'Referral Map', icon: ReferralMapIcon },
    ]
  },
  { 
    label: 'Pipeline', 
    icon: PipelineIcon,
    children: [
      { path: '/pipeline/soft-circle', label: 'Soft Circle', icon: SoftCircleIcon },
      { path: '/pipeline/hard-circle', label: 'Hard Circle', icon: HardCircleIcon },
    ]
  },
  { 
    label: 'Activities', 
    icon: ActivitiesIcon,
    children: [
      { path: '/activities/tasks', label: 'Tasks', icon: TasksIcon },
      { path: '/activities/calendar', label: 'Calendar', icon: CalendarIconCustom },
    ]
  },
  { 
    label: 'Comms Hub', 
    icon: CommsHubIcon,
    children: [
      { path: '/comms/inbox', label: 'Inbox', icon: InboxIconCustom },
      { path: '/comms/templates', label: 'Templates', icon: TemplateIcon },
      { path: '/activities/feed', label: 'Interaction Logging', icon: InteractionIcon },
    ]
  },
  { 
    path: '/analytics', 
    label: 'Analytics', 
    icon: AnalyticsIcon 
  },
  { 
    path: '/automations', 
    label: 'Automations', 
    icon: AutomationsIcon 
  },
  { 
    path: '/network', 
    label: 'Network', 
    icon: NetworkIcon 
  },
  { 
    path: '/ops-compliance', 
    label: 'Ops & Compliance', 
    icon: OpsComplianceIcon 
  },
];

// Sidebar Navigation
const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  const isMenuExpanded = (label) => expandedMenus[label];

  const isChildActive = (children) => {
    return children?.some(child => location.pathname === child.path);
  };

  // Single nav item (no children)
  const NavItem = ({ item, isChild = false }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;
    
    return (
      <button
        onClick={() => handleNavigation(item.path)}
        title={!isExpanded ? item.label : undefined}
        className={cn(
          'flex items-center w-full rounded-md text-sm transition-all duration-200',
          isExpanded 
            ? isChild ? 'gap-3 px-3 py-2 pl-10 justify-start' : 'gap-3 px-3 py-2 justify-start'
            : 'lg:justify-center lg:px-0 lg:py-2 gap-3 px-3 py-2 justify-start',
          isActive
            ? 'bg-white/10 text-white'
            : 'text-zinc-400 hover:bg-white/5 hover:text-white'
        )}
      >
        <Icon size={18} className="flex-shrink-0" />
        {isExpanded && <span className="whitespace-nowrap text-sm">{item.label}</span>}
        {!isExpanded && <span className="lg:hidden whitespace-nowrap text-sm">{item.label}</span>}
      </button>
    );
  };

  // Parent nav item with children
  const NavItemWithChildren = ({ item }) => {
    const Icon = item.icon;
    const isOpen = isMenuExpanded(item.label);
    const hasActiveChild = isChildActive(item.children);
    
    return (
      <div>
        <button
          onClick={() => toggleMenu(item.label)}
          title={!isExpanded ? item.label : undefined}
          className={cn(
            'flex items-center w-full rounded-md text-sm transition-all duration-200',
            isExpanded 
              ? 'gap-3 px-3 py-2 justify-start'
              : 'lg:justify-center lg:px-0 lg:py-2 gap-3 px-3 py-2 justify-start',
            hasActiveChild
              ? 'text-white'
              : 'text-zinc-400 hover:bg-white/5 hover:text-white'
          )}
        >
          <Icon size={18} className="flex-shrink-0" />
          {isExpanded && (
            <>
              <span className="whitespace-nowrap text-sm flex-1 text-left">{item.label}</span>
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </>
          )}
          {!isExpanded && (
            <span className="lg:hidden whitespace-nowrap text-sm flex-1 text-left">{item.label}</span>
          )}
        </button>
        
        {/* Children - only show when expanded */}
        {(isExpanded || mobileOpen) && isOpen && (
          <div className="mt-1 space-y-1">
            {item.children.map((child) => (
              <NavItem key={child.path} item={child} isChild={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className={cn(
          'fixed inset-y-0 left-0 z-50',
          'h-screen bg-[#0b0d10] border-r border-white/5 flex flex-col',
          'transition-all duration-300 ease-in-out',
          'lg:translate-x-0',
          isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
          mobileOpen ? 'translate-x-0 w-60' : '-translate-x-full lg:translate-x-0'
        )}
      >
        {/* User Profile Header */}
        <div className={cn(
          "border-b border-white/5 transition-all duration-300",
          isExpanded ? "p-3" : "lg:py-3 lg:px-2 p-3"
        )}>
          <div className={cn(
            "rounded-lg bg-[#0f1216] border border-white/5 transition-all duration-300",
            isExpanded ? "p-3" : "lg:p-2 p-3"
          )}>
            <div className={cn(
              "flex items-center transition-all duration-300",
              isExpanded ? "gap-3 justify-start" : "lg:justify-center justify-start gap-3"
            )}>
              <div className={cn(
                "rounded-full bg-[#1a1d24] border border-white/10 flex items-center justify-center flex-shrink-0 transition-all duration-300",
                isExpanded ? "w-9 h-9" : "lg:w-8 lg:h-8 w-9 h-9"
              )}>
                <User size={isExpanded ? 18 : 16} className="text-zinc-400" />
              </div>
              
              {isExpanded && (
                <div className="flex-1 min-w-0">
                  <h2 className="font-medium text-sm text-white truncate">OLO Admin</h2>
                  <p className="text-xs text-zinc-500 truncate">{user?.email || 'oliver@nasucapital...'}</p>
                </div>
              )}
              
              {!isExpanded && (
                <div className="flex-1 min-w-0 lg:hidden">
                  <h2 className="font-medium text-sm text-white truncate">OLO Admin</h2>
                  <p className="text-xs text-zinc-500 truncate">{user?.email || 'oliver@nasucapital...'}</p>
                </div>
              )}
            </div>
          </div>
          
          <button
            className="lg:hidden absolute top-3 right-3 p-2 text-zinc-400 hover:text-zinc-200 transition-colors"
            onClick={() => setMobileOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        {/* Main Navigation */}
        <nav className="flex-1 overflow-y-auto py-3">
          <ul className={cn(
            "space-y-1 transition-all duration-300",
            isExpanded ? "px-2" : "lg:px-2 px-2"
          )}>
            {navItems.map((item) => (
              <li key={item.label}>
                {item.children ? (
                  <NavItemWithChildren item={item} />
                ) : (
                  <NavItem item={item} />
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Settings & Logout Section */}
        <div className="border-t border-white/5 py-3">
          <div className={cn(
            "transition-all duration-300 space-y-1",
            isExpanded ? "px-2" : "lg:px-2 px-2"
          )}>
            {/* Settings Button */}
            <Link
              to="/settings"
              title={!isExpanded ? 'Settings' : undefined}
              className={cn(
                'w-full flex items-center rounded-md text-sm transition-all duration-200',
                location.pathname === '/settings'
                  ? 'bg-white/10 text-white'
                  : 'text-description hover:bg-white/5 hover:text-white',
                isExpanded 
                  ? 'gap-3 px-3 py-2 justify-start'
                  : 'lg:justify-center lg:px-0 lg:py-2 gap-3 px-3 py-2 justify-start'
              )}
            >
              <Settings size={18} className="flex-shrink-0" />
              {isExpanded && <span className="whitespace-nowrap">Settings</span>}
              {!isExpanded && <span className="lg:hidden whitespace-nowrap">Settings</span>}
            </Link>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              title={!isExpanded ? 'Logout' : undefined}
              className={cn(
                'w-full flex items-center rounded-md text-sm text-red-400 hover:bg-red-500/10 transition-all duration-200',
                isExpanded 
                  ? 'gap-3 px-3 py-2 justify-start'
                  : 'lg:justify-center lg:px-0 lg:py-2 gap-3 px-3 py-2 justify-start'
              )}
              data-testid="logout-button"
            >
              <LogoutIcon size={18} className="flex-shrink-0" />
              {isExpanded && <span className="whitespace-nowrap">Logout</span>}
              {!isExpanded && <span className="lg:hidden whitespace-nowrap">Logout</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Placeholder Page Component
const PlaceholderPage = ({ title }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
    className="flex items-center justify-center h-full min-h-[400px]"
  >
    <div className="text-center">
      <h2 className="text-2xl font-heading font-semibold text-zinc-100 mb-2">{title}</h2>
      <p className="text-zinc-500">Coming soon</p>
    </div>
  </motion.div>
);

// Main App Content (Protected)
const AppContent = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SessionWarning />
      <Sidebar mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="lg:ml-16 flex flex-col min-h-screen transition-all duration-300">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-30 bg-[#0a0a0a]/90 backdrop-blur-sm border-b border-white/6">
          <div className="flex items-center justify-between p-4">
            <button
              className="p-2 text-zinc-400 hover:text-zinc-200 transition-colors"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={24} />
            </button>
            <span className="font-heading font-semibold text-zinc-100">Nasu Capital</span>
            <div className="w-10" />
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 p-6 lg:p-10 overflow-y-auto">
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/saved-views" element={<PlaceholderPage title="Saved Views" />} />
              {/* Contacts */}
              <Route path="/contacts/all" element={<AllContacts />} />
              <Route path="/contacts/organizations" element={<Organizations />} />
              <Route path="/contacts/sentiment-score" element={<SentimentScore />} />
              <Route path="/contacts/segments" element={<SegmentsTags />} />
              <Route path="/contacts/referral-map" element={<ReferralMap />} />
              {/* Pipeline */}
              <Route path="/pipeline/soft-circle" element={<SoftCircle />} />
              <Route path="/pipeline/hard-circle" element={<HardCircle />} />
              {/* Activities */}
              <Route path="/activities/tasks" element={<Tasks />} />
              <Route path="/activities/feed" element={<InteractionLogging />} />
              <Route path="/activities/calendar" element={<CalendarPage />} />
              {/* Comms Hub */}
              <Route path="/comms/inbox" element={<Inbox />} />
              <Route path="/comms/templates" element={<Templates />} />
              {/* Other */}
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/automations" element={<PlaceholderPage title="Automations" />} />
              <Route path="/network" element={<Network />} />
              <Route path="/ops-compliance" element={<PlaceholderPage title="Ops & Compliance" />} />
              <Route path="/settings" element={<PlaceholderPage title="Settings" />} />
            </Routes>
          </AnimatePresence>
        </main>
      </div>

      <Toaster 
        position="bottom-center"
        theme="dark"
        richColors
        style={{ bottom: '10px' }}
        toastOptions={{
          style: {
            zIndex: 9999,
          }
        }}
      />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* /auth/callback removed - backend handles Google OAuth callback and redirects to / */}
            <Route path="/*" element={
              <ProtectedRoute>
                <AppContent />
              </ProtectedRoute>
            } />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
